import { useState, useEffect } from "react";
import { MessageSquare, TrendingUp, Clock, Hash } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API, useAuth } from "@/App";

export default function AdminChatAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getToken } = useAuth();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/chat-analytics`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="admin-chat-analytics">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Chat Analytics</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-gray-200 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="admin-chat-analytics">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Chat Analytics</h1>
        <p className="text-gray-500 mt-4">Failed to load analytics data.</p>
      </div>
    );
  }

  const topicEntries = Object.entries(data.popular_topics || {});
  const maxCount = topicEntries.length > 0 ? topicEntries[0][1] : 1;

  return (
    <div className="space-y-6" data-testid="admin-chat-analytics">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Chat Analytics</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-praroha-forest/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-praroha-forest" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{data.total_chats}</p>
              <p className="text-xs text-gray-500">Total Conversations</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-praroha-sage/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-praroha-sage" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{topicEntries.length}</p>
              <p className="text-xs text-gray-500">Detected Topics</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{data.recent_questions?.length || 0}</p>
              <p className="text-xs text-gray-500">Recent Questions</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Topics */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Hash className="w-4 h-4 text-praroha-forest" />
              <h2 className="font-heading text-lg font-semibold text-gray-900">Popular Topics</h2>
            </div>
            {topicEntries.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No topics detected yet. Topics appear as customers chat with Ankura.</p>
            ) : (
              <div className="space-y-3">
                {topicEntries.map(([topic, count]) => (
                  <div key={topic}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 capitalize">{topic}</span>
                      <span className="text-xs text-gray-500">{count} mention{count !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-praroha-forest rounded-full transition-all"
                        style={{ width: `${(count / maxCount) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Questions */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-praroha-forest" />
              <h2 className="font-heading text-lg font-semibold text-gray-900">Recent Customer Questions</h2>
            </div>
            {!data.recent_questions?.length ? (
              <p className="text-sm text-gray-400 py-4 text-center">No questions yet.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {data.recent_questions.map((q, i) => (
                  <div
                    key={i}
                    className="px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-100"
                    data-testid={`chat-question-${i}`}
                  >
                    {q}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
